import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Download, FileText, User, CalendarDays, Wallet, MinusCircle, Info, ClipboardList } from "lucide-react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import api from "@/lib/api";
import { locationOptions } from "@/lib/locations";

const LEFT = [["designation", "Designation"], ["department", "Department"], /* ["business_unit", "Business Unit"], */ ["joining_date", "Date of Joining"]];
const RIGHT = [/* ["pan_number", "PAN No."], */ ["bank_name", "Bank Name"], ["bank_account", "Bank A/c No."], ["ifsc", "IFSC"], ["location", "Location"]];
const EMP_KEYS = [...LEFT, ...RIGHT].map(([k]) => k).concat(["pf_number", "esi_number"]);
const AMT_KEYS = ["deduction", "extra_duty", "pf", "esi", "advance", "leave_balance"];
const DAY_BASE = 30;     // extra duty (Sunday) day-rate = salary / 30
const AUTO_RATE = 500;   // incentive per auto
const r2 = (v) => Math.round(v * 100) / 100;
const n = (v) => Number(v) || 0;
const fmt = (v) => n(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function inWords(num) {
  num = Math.round(num);
  if (!num) return "Zero";
  const a = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const two = (x) => (x < 20 ? a[x] : b[Math.floor(x / 10)] + (x % 10 ? " " + a[x % 10] : ""));
  const three = (x) => (x >= 100 ? a[Math.floor(x / 100)] + " Hundred" + (x % 100 ? " " : "") : "") + (x % 100 ? two(x % 100) : "");
  const out = [];
  const cr = Math.floor(num / 1e7); num %= 1e7;
  const lk = Math.floor(num / 1e5); num %= 1e5;
  const th = Math.floor(num / 1e3); num %= 1e3;
  if (cr) out.push(three(cr) + " Crore");
  if (lk) out.push(two(lk) + " Lakh");
  if (th) out.push(two(th) + " Thousand");
  if (num) out.push(three(num));
  return out.join(" ");
}

export default function PayslipEdit({ readOnly = false, pid, autoPdf = false, onPdf }) {
  const { id: routeId } = useParams();
  const id = pid || routeId;
  const nav = useNavigate();
  const ref = useRef(null);
  const [d, setD] = useState(null);
  const [p, setP] = useState({});
  const [e, setE] = useState({});
  const [printing, setPrinting] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    const { data } = await api.get(`/payslip/${id}`);
    setD(data); setE(data.employee || {});
    const pr = data.payroll; const a = n(pr.autos);
    setP({ ...pr, autos: a, incentive_base: Math.max(0, n(pr.incentive) - a * AUTO_RATE) });
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line
  const pdfRef = useRef(null);
  const autoDone = useRef(false);
  useEffect(() => {
    if (!autoPdf || !d || autoDone.current) return;
    const t = setTimeout(() => { if (autoDone.current) return; autoDone.current = true; pdfRef.current && pdfRef.current(); }, 400);
    return () => clearTimeout(t);
  }, [d]); // eslint-disable-line

  if (!d) return <p className="p-6 text-slate-400">Loading...</p>;

  const edit = !readOnly && !printing;
  const [yy, mm] = p.month.split("-");
  const dim = new Date(Date.UTC(+yy, +mm, 0)).getUTCDate();
  const sundays = Array.from({ length: dim }, (_, i) => new Date(Date.UTC(+yy, +mm - 1, i + 1)).getUTCDay()).filter((x) => x === 0).length;
  const manual = !!p.att_manual;
  const extraDays = n(p.auto_extra_duty_days ?? p.extra_duty_days);            // Sundays worked
  const wd = manual ? n(p.working_days) : dim - sundays;
  const present = manual ? n(p.present_days) : n(p.auto_present_days ?? p.present_days) + n(p.paid_leave_days) + extraDays;
  const woff = manual ? n(p.weekly_off) : Math.max(0, sundays - extraDays);
  const extraDim = (() => { const m = String(p.month || ""); if (!/^\d{4}-\d{2}/.test(m)) return 0; return Math.max(0, new Date(+m.slice(0, 4), +m.slice(5, 7), 0).getDate() - 30); })();
  const d31 = manual ? 0 : n(p.extra_day_count);
  const wd31 = !manual && extraDim > 0 && new Date(Date.UTC(+yy, +mm - 1, 31)).getUTCDay() !== 0 ? 1 : 0;
  const lopDays = Math.max(0, (wd - wd31) - (Math.max(0, present - extraDays) - d31));
  const lop = r2((n(p.salary) / 30) * lopDays);
  const extraDayPay = r2((n(p.salary) / 30) * d31);
  const extraManual = !!p.extra_duty_manual;
  const extraDuty = extraManual ? n(p.extra_duty) : r2((n(p.salary) / DAY_BASE) * extraDays);
  const autos = Math.max(0, Math.floor(n(p.autos)));
  const autoInc = autos * AUTO_RATE;
  const incBase = 0; // manual incentive removed
  const incentiveTotal = incBase + autoInc;
  const earn = n(p.salary) + incentiveTotal + extraDuty + extraDayPay;
  const ded = lop + n(p.pf) + n(p.esi) + n(p.advance) + n(p.deduction);
  const net = Math.max(0, earn - ded);
  const toggleAtt = () => setP(manual ? { ...p, att_manual: false } : { ...p, att_manual: true, working_days: wd, present_days: present, weekly_off: woff });
  const attVal = (k, v) => edit && manual
    ? <input type="number" step="0.5" value={p[k] ?? v} onChange={(ev) => setP({ ...p, [k]: ev.target.value })} className="w-20 text-center text-xl font-bold bg-white border border-emerald-300 rounded" />
    : <p className="text-2xl font-bold">{v}</p>;
  const slipNo = `RT39/${yy}/${mm}${String(e.employee_code || "").replace(/\D/g, "").slice(-2)}`;
  const today = new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  const monthLabel = new Date(p.month + "-01").toLocaleDateString("en-IN", { timeZone: "UTC", month: "short", year: "numeric" });

  const txt = (k) => edit && k === "location"
    ? <select value={e.location ?? ""} onChange={(ev) => setE({ ...e, location: ev.target.value })} className="w-full bg-white border border-emerald-200 rounded px-1.5 py-0.5 text-xs">
        <option value="">Select</option>
        {locationOptions(e.location).map((l) => <option key={l} value={l}>{l}</option>)}
      </select>
    : edit
    ? <input value={e[k] ?? ""} onChange={(ev) => setE({ ...e, [k]: ev.target.value })} className="w-full bg-white border border-emerald-200 rounded px-1.5 py-0.5 text-xs" />
    : <span>{e[k] || "—"}</span>;
  const amt = (k, editable = true) => edit && editable
    ? <input type="number" value={p[k] ?? 0} onChange={(ev) => setP({ ...p, [k]: ev.target.value })} className="w-28 text-right bg-white border border-emerald-200 rounded px-1.5 py-0.5 text-xs" />
    : <span>{fmt(p[k])}</span>;
  const kv = (label, node) => (
    <div className="flex items-center text-xs py-1"><span className="w-28 sm:w-32 shrink-0 text-slate-500">{label}</span><span className="mr-2 text-slate-400">:</span><span className="flex-1 min-w-0 break-words text-slate-800">{node}</span></div>
  );
  const head = (Icon, title) => (
    <div className="flex items-center gap-2 bg-emerald-700 text-white px-3 py-2 rounded-t-lg text-sm font-semibold"><Icon className="w-4 h-4" />{title}</div>
  );
  const row = (label, node) => (
    <tr className="border-t border-emerald-100"><td className="px-3 py-1.5">{label}</td><td className="px-3 py-1.5 text-right">{node}</td></tr>
  );

  const save = async () => {
    try {
      const body = { employee: Object.fromEntries(EMP_KEYS.map((k) => [k, e[k] ?? ""])) };
      AMT_KEYS.forEach((k) => { body[k] = n(p[k]); });
      body.incentive = incentiveTotal; body.incentive_base = incBase; body.autos = autos; body.mess = 0;
      Object.assign(body, { att_manual: manual, lop, lop_days: lopDays, extra_duty: extraDuty, extra_duty_manual: extraManual, extra_duty_days: extraDays, extra_day: extraDayPay, extra_day_count: d31 });
      if (manual) Object.assign(body, { working_days: n(p.working_days), present_days: n(p.present_days), weekly_off: n(p.weekly_off) });
      await api.put(`/payroll/${id}/adjust`, body);
      setMsg("Saved ✓"); await load();
    } catch (err) { setMsg("Save failed ✗ " + (err?.response?.status || "") + " " + JSON.stringify(err?.response?.data?.detail || err?.message || "")); return; }
    setTimeout(() => setMsg(""), 2500);
  };

  const pdf = async () => {
    setPrinting(true);
    await new Promise((r) => setTimeout(r, 150));
    if (!ref.current) { setPrinting(false); return; }
    const canvas = await html2canvas(ref.current, { scale: 2, backgroundColor: "#ffffff", useCORS: true, windowWidth: 1280 });
    setPrinting(false);
    const doc = new jsPDF("p", "mm", "a4");
    const r = Math.min(210 / canvas.width, 297 / canvas.height);
    doc.addImage(canvas.toDataURL("image/png"), "PNG", (210 - canvas.width * r) / 2, 0, canvas.width * r, canvas.height * r);
    const fname = `payslip-${e.employee_code}-${String(e.name || "").replace(/\s+/g, "_")}-${p.month}.pdf`;
    if (onPdf) return onPdf(doc.output("blob"), fname);
    doc.save(fname);
  };

  pdfRef.current = pdf;

  return (
    <div className="space-y-4 min-w-0">
      <div className="flex flex-wrap items-center gap-3">
        {!pid && <button onClick={() => nav(-1)} className="flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"><ArrowLeft className="w-4 h-4" /> Back</button>}
        <div className="flex-1" />
        {msg && <span className="text-sm text-emerald-700">{msg}</span>}
        {!readOnly && <button onClick={save} className="flex items-center gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white px-4 h-10 text-sm"><Save className="w-4 h-4" /> Save</button>}
        <button onClick={pdf} className="flex items-center gap-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white px-4 h-10 text-sm"><Download className="w-4 h-4" /> Download PDF</button>
      </div>

      <div ref={ref} style={printing ? { width: 900 } : undefined} className="max-w-4xl mx-auto bg-gradient-to-b from-emerald-50/60 to-white border border-emerald-100 rounded-2xl p-3 sm:p-6 text-slate-800">
        <div className="flex items-center justify-between pb-4 border-b border-emerald-100">
          <div>
            <div className="flex items-center gap-4">
            <img src="/route39-logo.png" alt="Route39" className="h-14 w-auto" />
            <div className="border-l border-emerald-200 pl-4 text-[10px] leading-snug text-slate-600 uppercase">
              <div>NO 1C MARIYA NAGAR,</div>
              <div>NEAR VIGNESHWARA TEX,</div>
              <div>VALAYANKADU SOUTH,</div>
              <div>TIRUPUR,</div>
              <div>TAMIL NADU – 641603.</div>
              <div className="mt-0.5 font-semibold text-slate-700">Ph: 8393943939</div>
            </div>
          </div>
            <div className="mt-1.5 flex items-center gap-2 text-[7px] sm:text-[10px] tracking-[0.18em] uppercase font-semibold whitespace-nowrap">
              <span className="text-slate-400">A Product of</span><span className="h-3 w-px bg-slate-400" /><span className="text-slate-900">Attendy Technologies Pvt Ltd</span>
            </div>
          </div>
          
        </div>

        <div className="flex flex-col items-center gap-2 mt-4 py-3 bg-emerald-50 border border-emerald-100 rounded-lg">
          <div className="flex items-center gap-2 bg-emerald-700 text-white px-4 sm:px-6 py-2.5 rounded-lg font-bold tracking-wide"><FileText className="w-5 h-5" /> SALARY PAYSLIP</div>
          <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 px-4 text-xs"><span><b>Payslip No:</b> {slipNo}</span><span className="text-slate-300">|</span><span><b>Date:</b> {today}</span></div>
        </div>

        <div className="mt-4 border border-emerald-100 rounded-lg">
          {head(User, "Employee Details")}
          <div className="grid md:grid-cols-2 gap-x-8 p-3">
            <div>{kv("Employee Name", <b>{e.name}</b>)}{kv("Employee ID", e.employee_code)}{LEFT.map(([k, l]) => <div key={k}>{kv(l, txt(k))}</div>)}</div>
            <div className="md:border-l md:pl-6 border-emerald-100">{RIGHT.map(([k, l]) => <div key={k}>{kv(l, txt(k))}</div>)}</div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div className="sm:col-span-3 border border-emerald-100 rounded-lg">
            <div className="flex flex-wrap items-center justify-between gap-1 bg-emerald-700 text-white px-3 py-2 rounded-t-lg text-sm font-semibold">
              <span className="flex items-center gap-2"><CalendarDays className="w-4 h-4" />Attendance Summary {manual && !readOnly && <span className="text-[10px] bg-amber-400 text-slate-900 rounded px-1.5">MANUAL</span>}</span>
              {edit && <button onClick={toggleAtt} className="text-xs bg-white/20 hover:bg-white/30 rounded px-2 py-0.5">{manual ? "↺ Auto (from Attendance)" : "✎ Edit Manually"}</button>}
            </div>
            <div className="grid grid-cols-3 text-center py-3 px-1 divide-x divide-emerald-100 [&_p.text-xs]:px-1">
              <div className="flex flex-col items-center">{attVal("working_days", wd)}<p className="text-xs text-slate-500">Total Working Days</p></div>
              <div className="flex flex-col items-center">{attVal("present_days", present)}<p className="text-xs text-slate-500">Present Days</p></div>
              <div className="flex flex-col items-center">{attVal("weekly_off", woff)}<p className="text-xs text-slate-500">Weekly Off</p></div>
            </div>
          </div>
          <div className="border border-emerald-100 rounded-lg text-center py-3 text-xs">
            <p className="text-slate-500">Pay Month</p><p className="text-lg font-bold">{monthLabel}</p>
            <div className="border-t border-emerald-100 my-2 mx-3" /><p className="text-slate-500">Days in Month</p><p className="text-lg font-bold">{dim}</p>
          </div>
        </div>

        <div className="mt-4 grid md:grid-cols-2 gap-4 items-start">
          <div className="border border-emerald-100 rounded-lg overflow-x-auto">
            {head(Wallet, "Earnings / Salary Components")}
            <table className="w-full text-xs">
              <thead><tr className="bg-emerald-50 font-semibold"><td className="px-3 py-1.5">Description</td><td className="px-3 py-1.5 text-right">Amount (₹)</td></tr></thead>
              <tbody>
                {row("Fixed Salary", amt("salary", false))}
                {d31 > 0 && row("31st Day Pay", <span>{fmt(extraDayPay)}</span>)}
                {/* {row(`Overtime (${n(p.overtime_hours)}h)`, amt("overtime", false))} */}
                {row("Extra Duty" /* `Extra Duty (${extraDays} Sundays)` */, edit
                  ? <span className="inline-flex items-center gap-1">{extraManual && <button onClick={() => setP({ ...p, extra_duty_manual: false })} className="text-[10px] text-emerald-700 underline">auto</button>}<input type="number" value={extraManual ? (p.extra_duty ?? 0) : extraDuty} onChange={(ev) => setP({ ...p, extra_duty: ev.target.value, extra_duty_manual: true })} className="w-24 sm:w-28 text-right bg-white border border-emerald-200 rounded px-1.5 py-0.5 text-xs" /></span>
                  : <span>{fmt(extraDuty)}</span>)}
                {(edit || autos > 0) && row(<span>No. of Autos <span className="text-slate-400">(× ₹{AUTO_RATE})</span></span>, edit
                  ? <input type="number" min="0" step="1" value={p.autos ?? 0} onChange={(ev) => setP({ ...p, autos: ev.target.value })} className="w-24 sm:w-28 text-right bg-white border border-emerald-200 rounded px-1.5 py-0.5 text-xs" />
                  : <span>{autos}</span>)}
                {(edit || autos > 0) && row("Incentive", <span>{fmt(autoInc)}</span>)}
                <tr className="border-t border-emerald-200 bg-emerald-50 font-bold"><td className="px-3 py-2">Total Earnings</td><td className="px-3 py-2 text-right">{fmt(earn)}</td></tr>
              </tbody>
            </table>
          </div>
          <div>
            <div className="border border-emerald-100 rounded-lg overflow-x-auto">
              {head(MinusCircle, "Deductions")}
              <table className="w-full text-xs">
                <thead><tr className="bg-emerald-50 font-semibold"><td className="px-3 py-1.5">Description</td><td className="px-3 py-1.5 text-right">Amount (₹)</td></tr></thead>
                <tbody>
                  {row(`LOP (${lopDays} days)`, <span>{fmt(lop)}</span>)}
                  {row("Provident Fund (PF)", amt("pf"))}
                  {row("ESI (Employee Share)", amt("esi"))}
                  {row("Salary Advance", amt("advance"))}
                  {row("Other Deduction", amt("deduction"))}
                  <tr className="border-t border-emerald-200 bg-emerald-50 font-bold"><td className="px-3 py-2">Total Deductions</td><td className="px-3 py-2 text-right">{fmt(ded)}</td></tr>
                </tbody>
              </table>
            </div>
            <div className="mt-3 flex items-center justify-between bg-emerald-700 text-white rounded-lg px-4 py-2.5 font-bold"><span>Net Pay</span><span>₹ {fmt(net)}</span></div>
            <div className="mt-2 flex items-stretch text-xs border border-emerald-100 rounded-lg overflow-hidden"><span className="shrink-0 whitespace-nowrap px-3 py-2 text-slate-500 border-r border-emerald-100 bg-emerald-50/50 flex items-center">Amount in Words</span><span className="flex-1 min-w-0 px-3 py-2 font-medium leading-snug break-words flex items-center">Rupees {inWords(net)} Only</span></div>
          </div>
        </div>

        {/* Other Details + Notes hidden */}
        {false && (
        <div className="mt-4 grid md:grid-cols-2 gap-4 border border-emerald-100 rounded-lg p-3">
          <div>
            <p className="flex items-center gap-2 font-semibold text-emerald-800 mb-1"><Info className="w-4 h-4" /> Other Details</p>
            {kv("Leave Balance (CL)", edit ? amt("leave_balance") : `${n(p.leave_balance)} days`)}
            {kv("PF Number", txt("pf_number"))}
            {kv("ESI Number", txt("esi_number"))}
          </div>
          <div className="md:border-l md:pl-4 border-emerald-100 text-xs">
            <p className="flex items-center gap-2 font-semibold text-emerald-800 mb-1 text-sm"><ClipboardList className="w-4 h-4" /> Notes</p>
            <ul className="list-disc pl-4 space-y-0.5 text-slate-600">
              <li>LOP, if any, will be adjusted in the applicable month.</li>
              {/* <li>Overtime is paid as per company policy.</li> */}
              <li>Deductions towards PF, ESI, Mess, etc. are as per statutory and company rules.</li>
              <li>For any discrepancies, please contact HR/Payroll team.</li>
            </ul>
          </div>
        </div>

        )}

        <div className="mt-4 grid grid-cols-3 gap-2 sm:gap-4 text-xs border-t border-emerald-100 pt-3">
          <div><p className="font-semibold">Prepared By</p><p className="text-slate-500">HR &amp; Payroll Team<br />Route 39</p></div>
          <div><p className="font-semibold">Verified By</p><div className="border-t border-slate-300 mt-6 pt-1 text-slate-500">HR Manager</div></div>
          <div><p className="font-semibold">Authorized Signatory</p><div className="border-t border-slate-300 mt-6 pt-1 text-slate-500">Founder / Director</div></div>
        </div>


      </div>
    </div>
  );
}
